import React, { useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, List, Loader2 } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  MetricsRow, CompetencyChart,
  TopPerformersCard, AlertsCard, DrillCard,
} from '../../../components/LevelDashboard';
import { useGetKvsResult6to8SchoolDetailQuery } from '../../../services/kvsResult6to8Api';
import type { School6to8Student } from '../../../services/kvsResult6to8Api';
import { CLASS_NAMES } from '../data/index';
import type { LevelMetrics, CompetencyBucket, TopPerformer, AlertItem } from '../../../types';

const BASE = '/kvs-samagam/student-analytics/stages/middle';
const levelSlug = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

const SchoolDrillDown: React.FC = () => {
  const { regionId, schoolId } = useParams<{ regionId: string; schoolId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const academicYear = Number(searchParams.get('year') || '2025');
  const termValue = searchParams.get('term') || 'overall';
  const term = termValue === 'overall' ? 2 : Number(termValue);
  const querySuffix = `?year=${academicYear}&term=${termValue}`;

  const { data: schoolRes, isLoading } = useGetKvsResult6to8SchoolDetailQuery(
    schoolId ? { year: academicYear, term, kvid: Number(schoolId) } : skipToken
  );

  const d = schoolRes?.data;

  const metrics: LevelMetrics = useMemo((): LevelMetrics => {
    if (!d) return { totalStudents: 0, avgAttendance: 0, avgCompetency: 0, needsSupportCount: 0, excellentCount: 0, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
    const classes = Object.values(d.byClass);
    const totalStudents = classes.reduce((s, c) => s + c.totalStudents, 0);
    const totalWeightedMarks = classes.reduce((s, c) => s + c.avgMarks * c.totalStudents, 0);
    const avgCompetency = totalStudents > 0 ? +(totalWeightedMarks / totalStudents).toFixed(1) : 0;
    const needsSupportCount = classes.reduce((s, c) => s + c.needSupport, 0);
    const excellentCount = classes.reduce((s, c) => s + c.excellentStudents, 0);
    const allStudents = classes.reduce<School6to8Student[]>((acc, c) => acc.concat(c.students || []), []);
    const attendanceSum = allStudents.reduce((s, st) => s + (st.workingDay > 0 ? (st.presentDay / st.workingDay) * 100 : 0), 0);
    const avgAttendance = allStudents.length > 0 ? +(attendanceSum / allStudents.length).toFixed(1) : 0;
    return { totalStudents, avgAttendance, avgCompetency, needsSupportCount, excellentCount, avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0 };
  }, [d]);

  const competencyBuckets = useMemo((): CompetencyBucket[] => {
    const empty: CompetencyBucket[] = [
      { level: 'Excellent', count: 0, percent: 0 }, { level: 'Good', count: 0, percent: 0 },
      { level: 'Average', count: 0, percent: 0 }, { level: 'Below Average', count: 0, percent: 0 },
    ];
    if (!d) return empty;
    const allStudents = Object.values(d.byClass).reduce<School6to8Student[]>((acc, c) => acc.concat(c.students || []), []);
    const total = allStudents.length;
    if (total === 0) return empty;
    const excellent = allStudents.filter(s => s.marks >= 85).length;
    const good = allStudents.filter(s => s.marks >= 70 && s.marks < 85).length;
    const average = allStudents.filter(s => s.marks >= 55 && s.marks < 70).length;
    const belowAverage = allStudents.filter(s => s.marks < 55).length;
    const pct = (n: number) => +((n / total) * 100).toFixed(1);
    return [
      { level: 'Excellent', count: excellent, percent: pct(excellent) },
      { level: 'Good', count: good, percent: pct(good) },
      { level: 'Average', count: average, percent: pct(average) },
      { level: 'Below Average', count: belowAverage, percent: pct(belowAverage) },
    ];
  }, [d]);

  const topPerformers = useMemo((): TopPerformer[] => {
    if (!d) return [];
    return Object.values(d.byClass)
      .reduce<School6to8Student[]>((acc, c) => acc.concat(c.students || []), [])
      .sort((a, b) => b.marks - a.marks)
      .slice(0, 5)
      .map(s => ({ id: String(s.studentID), name: s.studentName || `Roll No ${s.rollNo}`, metric: 'Marks', value: s.marks }));
  }, [d]);

  const alerts = useMemo((): AlertItem[] => {
    if (!d) return [];
    return Object.values(d.byClass)
      .reduce<School6to8Student[]>((acc, c) => acc.concat(c.students || []), [])
      .filter(s => s.marks < 60)
      .sort((a, b) => a.marks - b.marks)
      .slice(0, 5)
      .map(s => ({
        id: String(s.studentID), name: s.studentName || `Roll No ${s.rollNo}`,
        issue: `${s.marks.toFixed(1)}%`,
        severity: (s.marks < 40 ? 'high' : 'medium') as AlertItem['severity'],
      }));
  }, [d]);

  const classCards = useMemo(() => {
    if (!d) return [];
    return Object.entries(d.byClass).map(([classKey, classData]) => {
      const allSt = classData.students || [];
      const total = allSt.length;
      const attSum = allSt.reduce((s, st) => s + (st.workingDay > 0 ? (st.presentDay / st.workingDay) * 100 : 0), 0);
      const classMetrics: LevelMetrics = {
        totalStudents: classData.totalStudents, avgCompetency: +classData.avgMarks.toFixed(1),
        needsSupportCount: classData.needSupport, excellentCount: classData.excellentStudents,
        avgAttendance: total > 0 ? +(attSum / total).toFixed(1) : 0,
        avgBehaviorScore: 0, avgPeerScore: 0, bmiNormalPercent: 0,
      };
      const classAlerts: AlertItem[] = allSt.filter(s => s.marks < 60).slice(0, 3)
        .map(s => ({ id: String(s.studentID), name: s.studentName || '', issue: `${s.marks.toFixed(1)}%`, severity: (s.marks < 40 ? 'high' : 'medium') as AlertItem['severity'] }));
      return { classKey, classMetrics, classAlerts };
    });
  }, [d]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading school data…</span>
      </div>
    );
  }

  if (!d) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">School not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-600 text-sm mt-2 hover:underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(location.pathname.slice(0, location.pathname.lastIndexOf(`/school/${schoolId}`)) + location.search)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{d.schoolName}</h1>
          <p className="text-sm text-gray-500">Middle Stage &mdash; Region: {d.roName} &mdash; {metrics.totalStudents.toLocaleString()} students</p>
        </div>
        <div className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select id="term" value={termValue} onChange={(e) => navigate(`${location.pathname}?year=${academicYear}&term=${e.target.value}`)} className="text-sm text-gray-700 bg-transparent focus:outline-none">
            <option value="overall">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>
      </div>

      <MetricsRow m={metrics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CompetencyChart buckets={competencyBuckets} onBarClick={(lvl) => navigate(`${BASE}/region/${regionId}/school/${schoolId}/list/classes/${levelSlug(lvl)}${querySuffix}`)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={topPerformers} />
        <AlertsCard items={alerts} onViewAll={() => navigate(`${BASE}/region/${regionId}/school/${schoolId}/list/classes/below-average${querySuffix}`)} viewAllLabel="Need Attention Classes" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Classes — Drill Down</h2>
          <button onClick={() => navigate(`${BASE}/region/${regionId}/school/${schoolId}/list/classes${querySuffix}`)} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition">
            <List size={16} /> See All Classes
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {classCards.map(({ classKey, classMetrics, classAlerts }) => (
            <DrillCard key={classKey} title={CLASS_NAMES[classKey] || `Class ${classKey}`} subtitle={`${classMetrics.totalStudents} students`} metrics={classMetrics} alerts={classAlerts} navigateTo={`class/${classKey}${querySuffix}`} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SchoolDrillDown;
